/**
 * RF-SLC20260926-012 — Vista de faturas de clientes SEM e-mail (UI-12-01).
 *
 * Lista as faturas **emitidas mas não entregues** ao cliente por ausência de
 * e-mail utilizável (`documento_erp IS NOT NULL AND notificado = 0`), para
 * impressão e envio postal. Vista de CONSULTA — sem "Tentar novamente" nem
 * qualquer ação de escrita (RB-04). Mantém "Transferir PDF" (RB-05) e
 * "Exportar" (RB-06).
 *
 * Irmã de `FinanceiroFaturacaoAutomaticaListView` (RF-SLC20260926-004): mesmo
 * padrão `table()`/`options()` server-side e mesmo estilo de tabela, mas chama
 * os métodos `*SemEmail` do modelo, que aplicam o filtro fixo no servidor. As 9
 * colunas são as do RB-03 (sem colaborador/estado/referência).
 *
 * Registo/routing no módulo-pai `financeiro` (`index.js` + `financeiro.routes.js`);
 * props injetadas: `['CoreFaturacaoAutomatica', 'UIService']`.
 *
 * Spec: specs/01_requirements/RF-SLC20260926-012-vista-faturas-sem-email.md
 * UI:   specs/04_ux_ui/web/ui-12-01-vista-sem-email.md
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Chip, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { mdiDownload, mdiMailbox, mdiMailboxUpOutline } from '@mdi/js';
import moment from 'moment';
import { useModal } from 'mui-modal-provider';
import { ConfirmDialog, MdiIcon, Table, WaitDialog } from '@components';
// Role de ESCRITA, distinta da `coreVerFaturacaoAutomatica` que dá acesso à
// vista: ver a lista não autoriza fechar casos.
const ROLE_MARCAR_ENVIO_POSTAL = 'coreMarcarEnvioPostalFaturacaoAutomatica';
// ── Constantes ────────────────────────────────────────────────────────────────
const ESTADO_CONCLUIDO = 4;
// Extensão (DEP) da pasta de PDFs por departamento — espelha
// `DEP_POR_DEPARTAMENTO` em `codebase/server/boot/faturacao_automatica.js`.
const DEP_POR_DEPARTAMENTO = { 1: 'ie', 2: 'for', 6: 'gas' };
const DEPARTAMENTOS_OPCOES = [
    { value: 1, label: 'Inspeções Elétricas' },
    { value: 2, label: 'Formação' },
    { value: 6, label: 'Gás' }
];
// ── Helpers de apresentação ───────────────────────────────────────────────────
/** Texto vazio → "-". */
const txt = (v) => {
    if (v === null || v === undefined || ('' + v).trim() === '')
        return '-';
    return '' + v;
};
/** Data (só dia) vazia/invalida → "--/--/----". */
const dataDia = (v) => {
    if (!v)
        return '--/--/----';
    const m = moment(v);
    return m.isValid() ? m.format('DD/MM/YYYY') : '--/--/----';
};
/** Data com hora vazia/inválida → "-". */
const dataHora = (v) => {
    if (!v)
        return '-';
    const m = moment(v);
    return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '-';
};
/** Soma do total das linhas. */
const totalPedido = (linhas) => (linhas || []).reduce((s, l) => s + (Number(l.itemQuantidade) || 0) * (Number(l.itemPrecoNet) || 0), 0);
const moeda = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v);
// ── Componente principal ─────────────────────────────────────────────────────
const FinanceiroFaturacaoSemEmailListView = ({ CoreFaturacaoAutomatica, UIService, AuthorizationService }) => {
    const { showModal } = useModal();
    const [data, setData] = useState();
    const [loading, setLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const [options, setOptions] = useState({ filter: [], sort: [] });
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
    const [filter, setFilter] = useState([]);
    const [sort, setSort] = useState([]);
    // Traduz o operador do MRT para a DSL do método `tableSemEmail` do servidor.
    const getType = (type) => {
        switch (type) {
            case 'contains':
                return '$ilike';
            case 'startsWith':
                return '$sw';
            case 'notEquals':
                return '$not';
            case 'empty':
                return '$null';
            case 'notEmpty':
                return '$notNull';
            case 'greaterThan':
                return '$gt';
            case 'greaterThanOrEqualTo':
                return '$gte';
            case 'lessThan':
                return '$lt';
            case 'lessThanOrEqualTo':
                return '$lte';
            default:
                return '$eq';
        }
    };
    const initialFetch = async () => {
        if (loading) {
            setLoading(false);
            try {
                const opt = await CoreFaturacaoAutomatica.optionsSemEmail().$promise;
                setOptions(opt);
            }
            catch (e) {
                // `optionsSemEmail()` pode não estar disponível — mantém o default.
            }
        }
    };
    const fetchData = async () => {
        try {
            const filters = {};
            filter.forEach(r => {
                filters[r.id] = `${getType(r.type)}:${r.value}`;
            });
            const sorter = [];
            sort.forEach(r => {
                sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
            });
            const aux = await CoreFaturacaoAutomatica.tableSemEmail({
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                filter: filters,
                sortBy: sorter
            }).$promise;
            setData(aux);
        }
        catch (e) {
            UIService.addToast('Erro a carregar faturas sem e-mail: ' + (e?.data?.error?.message || e?.message || ''));
        }
        setRefetching(false);
    };
    useEffect(() => {
        initialFetch();
        if (!refetching) {
            setRefetching(true);
            fetchData();
        }
    }, [pagination, filter, sort]);
    // ── Ações ──────────────────────────────────────────────────────────────────
    /**
     * Regista, ou anula, o envio da fatura por correio (CR-SLD20180014-0005).
     * A linha NÃO sai da lista — muda de estado e continua visível; é o filtro
     * rápido que separa o que está por tratar do que já foi.
     */
    const onMarcarEnvioPostal = async (pedido, marcado) => {
        try {
            const r = await CoreFaturacaoAutomatica.marcarEnvioPostal({ ids: [pedido.id], marcado }).$promise;
            UIService.addToast(r && r.alterados === 0
                ? `A fatura ${txt(pedido.documentoErp)} já estava nesse estado — listagem atualizada.`
                : marcado
                    ? `Fatura ${txt(pedido.documentoErp)} registada como enviada por correio.`
                    : `Registo de envio por correio da fatura ${txt(pedido.documentoErp)} anulado.`);
            setRefetching(true);
            await fetchData();
        }
        catch (e) {
            UIService.addToast('Erro ao registar o envio postal: ' + (e?.data?.error?.message || e?.message || ''));
        }
    };
    /**
     * Marcar não pede confirmação: é a ação corrente — imprime-se um lote e
     * marca-se fatura a fatura —, é reversível e fica auditada. Anular é raro e
     * significa "enganei-me"; é aí que a pausa vale alguma coisa.
     */
    const onAlternarEnvioPostal = (pedido) => {
        const marcado = Number(pedido.envioPostal) === 1;
        if (!marcado)
            return onMarcarEnvioPostal(pedido, true);
        console.log("Show Modal?");
        showModal(ConfirmDialog, {
            title: 'Anular registo de envio postal',
            message: `Tem a certeza que pretende anular o registo de envio por correio da fatura ${txt(pedido.documentoErp)}? A anulação fica registada no histórico.`,
            onClose: async (result) => {
                if (result)
                    await onMarcarEnvioPostal(pedido, false);
            }
        });
    };
    const onTransferir = (pedido) => {
        const dep = pedido.departamentoId != null ? DEP_POR_DEPARTAMENTO[pedido.departamentoId] : undefined;
        if (!dep || !pedido.ficheiroUuid) {
            UIService.addToast('Fatura sem PDF disponível para transferência.');
            return;
        }
        const url = `/api/Upload/faturas_automaticas_${dep}/download/${pedido.ficheiroUuid}.pdf`;
        window.open(url, '_blank');
        UIService.addToast(`A transferir fatura do pedido #${pedido.id}.`);
    };
    // ── Colunas (UI-12-01 / RB-03) ────────────────────────────────────────────────
    const columns = useMemo(() => [
        {
            accessorKey: 'departamentoId',
            exportKey: 'departamento.designacao',
            header: 'Departamento',
            size: 160,
            filterSelectOptions: DEPARTAMENTOS_OPCOES,
            Cell: ({ row }) => <Typography>{txt(row.original?.departamento?.designacao)}</Typography>
        },
        {
            accessorKey: 'numProcesso',
            header: 'Nº Processo',
            size: 140,
            Cell: ({ row }) => <Typography>{txt(row.original?.numProcesso)}</Typography>
        },
        {
            accessorKey: 'nomeCliente',
            header: 'Designação Cliente',
            size: 220,
            Cell: ({ row }) => <Typography>{txt(row.original?.nomeCliente)}</Typography>
        },
        {
            accessorKey: 'morada',
            header: 'Morada Cliente',
            size: 240,
            Cell: ({ row }) => <Typography>{txt(row.original?.morada)}</Typography>
        },
        {
            accessorKey: 'nif',
            header: 'NIF Cliente',
            size: 120,
            muiTableBodyCellProps: { align: 'right' },
            Cell: ({ row }) => <Typography>{txt(row.original?.nif)}</Typography>
        },
        {
            accessorKey: 'precoTotal',
            header: 'Preço Total',
            size: 120,
            // Computada (soma das linhas, pós-calculada no `tableSemEmail()`).
            // Não filtrável/ordenável server-side — ver OQ-SLC20260926-033.
            enableColumnFilter: false,
            enableSorting: false,
            muiTableBodyCellProps: { align: 'right' },
            Cell: ({ row }) => {
                const t = row.original?.precoTotal != null ? Number(row.original.precoTotal) : totalPedido(row.original?.linhas);
                return <Typography>{t > 0 ? moeda(t) : '-'}</Typography>;
            }
        },
        {
            accessorKey: 'dataRegisto',
            header: 'Data Pedido',
            size: 160,
            muiTableBodyCellProps: { align: 'right' },
            Cell: ({ row }) => <Typography>{dataDia(row.original?.dataRegisto)}</Typography>
        },
        {
            accessorKey: 'documentoErp',
            header: 'Nº Fatura',
            size: 140,
            Cell: ({ row }) => <Typography>{row.original?.documentoErp ? row.original.documentoErp : '—'}</Typography>
        },
        {
            accessorKey: 'dataFaturacao',
            header: 'Data Fatura',
            size: 160,
            enableColumnFilter: false,
            enableSorting: false,
            muiTableBodyCellProps: { align: 'right' },
            // Data/hora da emissão da fatura (data_criacao, preenchida pelo worker).
            Cell: ({ row }) => <Typography>{row.original?.estadoId === ESTADO_CONCLUIDO && row.original?.dataCriacao ? dataDia(row.original.dataCriacao) : '—'}</Typography>
        },
        // ── Envio postal (CR-SLD20180014-0005) ──────────────────────────────────
        // Sem `enableColumnFilter`/`enableSorting` de propósito: o `Table`
        // sobrepõe-se ao que se declarar aqui e usa o que o servidor devolve em
        // `optionsSemEmail()`/`sortableColumns`. Declarar no cliente não chega.
        {
            accessorKey: 'envioPostal',
            header: 'Envio Postal',
            exportKey: 'envioPostalTexto',
            size: 150,
            Cell: ({ row }) => Number(row.original?.envioPostal) === 1
                ? <Chip size='small' color='success' variant='outlined' label='Enviada'/>
                : <Chip size='small' variant='outlined' label='Por tratar'/>
        },
        {
            accessorKey: 'envioPostalData',
            header: 'Marcado em',
            exportKey: 'envioPostalDataTexto',
            size: 160,
            muiTableBodyCellProps: { align: 'right' },
            Cell: ({ row }) => <Typography>{dataHora(row.original?.envioPostalData)}</Typography>
        },
        {
            accessorKey: 'envioPostalFuncionario.name',
            header: 'Marcado por',
            size: 180,
            // O histórico completo é TEXT e cresce a cada alteração — daria uma
            // coluna ilegível e uma exportação pesada. Fica no tooltip, que é onde
            // se procura quando se quer perceber uma linha em concreto.
            Cell: ({ row }) => (<Tooltip title={row.original?.envioPostalHistorico ?? ''}>
            <Typography>{txt(row.original?.envioPostalFuncionario?.name)}</Typography>
          </Tooltip>)
        }
    ], []);
    // ── Exportação (UI-12-01 / RB-06) ─────────────────────────────────────────────
    const onExportar = async () => {
        const wait = showModal(WaitDialog, { title: 'Por favor aguarde...' });
        try {
            const filters = {};
            filter.forEach(r => {
                filters[r.id] = `${getType(r.type)}:${r.value}`;
            });
            const sorter = [];
            sort.forEach(r => {
                sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
            });
            const o = await CoreFaturacaoAutomatica.exportSemEmail({
                columns: columns.map(r => ({
                    accessorKey: r.exportKey ?? r.accessorKey,
                    header: r.header
                })),
                filter: filters,
                sortBy: sorter
            }).$promise;
            if (o) {
                const binary = atob(o.$data);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) {
                    bytes[i] = binary.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `faturacao-sem-email-exportacao-${moment().format('YYYY-MM-DD-HH-mm-ss')}.xlsx`;
                link.click();
            }
        }
        catch (e) {
            UIService.addToast(e?.data?.error?.message || e?.data?.message || 'Ocorreu um erro ao exportar dados!');
        }
        wait.destroy();
    };
    // ── Render ─────────────────────────────────────────────────────────────────
    return (<Box>
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant='h5'>Faturas sem E-mail</Typography>
        <Breadcrumbs aria-label='breadcrumb'>
          <Link underline='none' color='inherit'>
            Financeiro
          </Link>
          <Link underline='hover' color='text.primary' aria-current='page'>
            Faturas sem E-mail
          </Link>
        </Breadcrumbs>
      </Box>

      <Box>
        <Table storageKey='app.financeiro.faturacaosememail' 
    // Dois botões na coluna de ações — o default de 70px do `Table` chega
    // para um só e corta o segundo.
    rowActionSize={110} data={data} columns={columns} initial={{ sort, filter }} isLoading={refetching} options={options} onPaginate={setPagination} onFilter={setFilter} onSort={setSort} onExport={onExportar} 
    // A fatura marcada como enviada por correio CONTINUA na lista
    // (CR-SLD20180014-0005): o critério do servidor é "não entregue por
    // e-mail", que o envio postal não altera. São estes atalhos que devolvem
    // à Financeira a leitura de lista de trabalho — sem preset ativo,
    // vê-se tudo.
    quickFilterPresets={[
            {
                key: 'envio-postal-por-tratar',
                label: 'Por tratar',
                tooltip: 'Só as faturas ainda sem registo de envio por correio',
                filters: [{ id: 'envioPostal', value: 0, type: 'equals' }]
            },
            {
                key: 'envio-postal-enviadas',
                label: 'Enviadas por correio',
                tooltip: 'Só as faturas já registadas como enviadas por correio',
                filters: [{ id: 'envioPostal', value: 1, type: 'equals' }]
            }
        ]} actionItems={({ row }) => {
            const pedido = row.original;
            const podeTransferir = !!pedido.ficheiroUuid;
            const enviada = Number(pedido.envioPostal) === 1;
            // Sem a role de escrita o botão fica visível mas inerte — mais claro
            // do que deixar clicar e devolver 401.
            const podeMarcar = AuthorizationService.canPerform(ROLE_MARCAR_ENVIO_POSTAL);
            return [
                <Tooltip key='postal' title={!podeMarcar
                        ? 'Sem permissão para registar o envio postal'
                        : enviada ? 'Anular registo de envio por correio' : 'Registar envio por correio'}>
                <span>
                  <IconButton size='small' color={enviada ? 'success' : 'default'} disabled={!podeMarcar} onClick={() => onAlternarEnvioPostal(pedido)}>
                    <MdiIcon path={enviada ? mdiMailbox : mdiMailboxUpOutline} size={1}/>
                  </IconButton>
                </span>
              </Tooltip>,
                <Tooltip key='dl' title={podeTransferir ? 'Transferir fatura' : 'Sem PDF disponível'}>
                <span>
                  <IconButton size='small' disabled={!podeTransferir} onClick={() => onTransferir(pedido)}>
                    <MdiIcon path={mdiDownload} size={1}/>
                  </IconButton>
                </span>
              </Tooltip>
            ];
        }}/>
      </Box>
    </Box>);
};
export default FinanceiroFaturacaoSemEmailListView;
